<template>
  <!-- 내선 관리 › 설치 장소 sub-tab. 번호 관리(AgentPhoneNumbersView) 보유 번호 탭과 같은 master-detail.
       목록·전화기 수는 호스트가 받은 inventory 를 쓰고, 조작 뒤 재조회도 inventory 로 모은다. -->
  <div class="pbx-sites">
    <!-- 좌: 설치 장소 목록 -->
    <section class="pbx-sites__list" aria-label="설치 장소 목록">
      <header class="pbx-sites__list-header">
        <h2 class="pbx-sites__title">설치 장소</h2>
        <!-- 삭제(DELETE)는 서버에서 status=revoked 로 남는다 — 기본은 숨기고 필요할 때만 본다 -->
        <a-checkbox v-if="revokedCount" v-model:checked="showRevoked" class="pbx-sites__filter">
          폐기 포함 ({{ revokedCount }})
        </a-checkbox>
        <button
          type="button"
          class="pbx-sites__mini"
          title="새로고침"
          aria-label="설치 장소 목록 새로고침"
          :disabled="listLoading"
          @click="retry"
        >
          <ReloadOutlined />
        </button>
        <button type="button" class="pbx-sites__add" @click="openCreate">
          <PlusOutlined />
          <span>설치 장소 추가</span>
        </button>
      </header>

      <!-- 재조회 실패 — 이미 받은 목록은 그대로 두고 위에 알린다 -->
      <div v-if="listError && sites.length" class="pbx-sites__banner" role="alert">
        <span>{{ LIST_ERROR_TEXT }}</span>
        <button type="button" class="pbx-sites__link" :disabled="listLoading" @click="retry">다시 시도</button>
      </div>

      <div v-if="listError && !sites.length" class="pbx-sites__state pbx-sites__state--error" role="alert">
        <p>{{ LIST_ERROR_TEXT }}</p>
        <button type="button" class="pbx-sites__ghost" :disabled="listLoading" @click="retry">다시 시도</button>
      </div>
      <a-skeleton v-else-if="firstLoading" active :title="false" :paragraph="{ rows: 4 }" />
      <div v-else-if="!sites.length" class="pbx-sites__state">
        <p>{{ EMPTY_TEXT }}</p>
      </div>
      <div v-else class="pbx-sites__items">
        <button
          v-for="s in sites"
          :key="s.id"
          type="button"
          class="pbx-sites__item"
          :class="{ 'is-active': s.id === selectedId }"
          :aria-pressed="s.id === selectedId"
          @click="selectedId = s.id"
        >
          <span class="pbx-sites__item-top">
            <span class="pbx-sites__item-name">{{ s.name }}</span>
            <PbxBadge :badge="siteStatusBadge(s.status)" />
          </span>
          <span class="pbx-sites__item-fqdn" :title="`SIP 서버 ${s.sip_server_fqdn}`">{{ s.sip_server_fqdn }}</span>
          <span class="pbx-sites__item-meta">
            <PbxBadge :badge="siteTransport(s)" />
            <span>전화기 {{ deviceCountLabel(s.id) }}</span>
            <span>토큰 버전 {{ s.token_version }}</span>
          </span>
        </button>
      </div>
    </section>

    <!-- 우: 상세 -->
    <section class="pbx-sites__detail" aria-label="설치 장소 상세">
      <div v-if="!selected" class="pbx-sites__empty">
        <EnvironmentOutlined class="pbx-sites__empty-icon" />
        <p>설치 장소를 선택하세요</p>
        <small>프로비저닝 URL, SIP 서버 설정, 공인 IP, 토큰을 관리할 수 있습니다.</small>
      </div>

      <template v-else>
        <div class="pbx-sites__detail-header">
          <h2 class="pbx-sites__detail-title">{{ selected.name }}</h2>
          <div class="pbx-sites__detail-sub">
            <PbxBadge :badge="siteStatusBadge(selected.status)" />
            <span :title="formatPbxRelative(selected.created_at, now)">
              만든 시각 {{ formatPbxTime(selected.created_at) }}
            </span>
          </div>
        </div>

        <div v-if="selectedRevoked" class="pbx-sites__banner pbx-sites__banner--muted" role="status">
          <span>{{ REVOKED_TEXT }}</span>
        </div>

        <div class="pbx-sites__sections">
          <!-- 프로비저닝 URL — 원문은 열람할 때만 받는다(감사 로그) -->
          <div class="pbx-sites__section">
            <div class="pbx-sites__section-row">
              <div>
                <h3 class="pbx-sites__section-title">프로비저닝 URL</h3>
                <p class="pbx-sites__section-desc">
                  전화기 관리자 메뉴 Server Address 에 입력하는 주소입니다. 열람 기록은 감사 로그에 남습니다.
                </p>
              </div>
              <button
                type="button"
                class="pbx-sites__ghost"
                :disabled="selectedRevoked || isPending(`url:${selected.id}`)"
                @click="viewUrl(selected)"
              >
                <EyeOutlined />
                <span>프로비저닝 URL 보기</span>
              </button>
            </div>
            <code class="pbx-sites__masked">{{ MASKED_URL }}</code>
          </div>

          <!-- SIP 서버 -->
          <div class="pbx-sites__section">
            <div class="pbx-sites__section-row">
              <h3 class="pbx-sites__section-title">SIP 서버</h3>
              <button type="button" class="pbx-sites__ghost" :disabled="selectedRevoked" @click="openEdit('sip', selected)">수정</button>
            </div>
            <dl class="pbx-sites__info">
              <div>
                <dt>SIP 서버</dt>
                <dd>
                  {{ selected.sip_server_fqdn }}
                  <span class="pbx-sites__dd-hint">PBX 호스트의 속성이라 설치 장소에서 바꾸지 않습니다</span>
                </dd>
              </div>
              <div>
                <dt>기본 전송</dt>
                <dd><PbxBadge :badge="siteTransport(selected)" /></dd>
              </div>
              <div>
                <dt>SRTP 필수</dt>
                <dd>{{ selected.srtp_require ? '켜짐' : '꺼짐' }}</dd>
              </div>
              <div>
                <dt>NTP 서버</dt>
                <dd>{{ selected.ntp_server || '—' }}</dd>
              </div>
              <div>
                <dt>시간대 오프셋</dt>
                <dd>{{ gmtOffsetLabel(selected.gmt_offset_sec) }}</dd>
              </div>
              <div>
                <dt>설정 수신 주기</dt>
                <dd>{{ selected.polling_period_sec != null ? `${selected.polling_period_sec}초` : '—' }}</dd>
              </div>
            </dl>
          </div>

          <!-- 공인 IP -->
          <div class="pbx-sites__section">
            <div class="pbx-sites__section-row">
              <div>
                <h3 class="pbx-sites__section-title">공인 IP</h3>
                <p class="pbx-sites__section-desc">진단·경고용 — NSG 의 원천이 아님</p>
              </div>
              <button type="button" class="pbx-sites__ghost" :disabled="selectedRevoked" @click="openEdit('ips', selected)">수정</button>
            </div>
            <ul v-if="selected.public_ips?.length" class="pbx-sites__ips">
              <li v-for="ip in selected.public_ips" :key="ip">{{ ip }}</li>
            </ul>
            <p v-else class="pbx-sites__muted">등록된 공인 IP 가 없습니다.</p>
            <dl class="pbx-sites__info">
              <div>
                <dt>설치 장소 IP 에서만 설정 허용</dt>
                <dd>{{ selected.restrict_provisioning_to_site_ips ? '켜짐' : '꺼짐' }}</dd>
              </div>
            </dl>
          </div>

          <!-- 토큰 -->
          <div class="pbx-sites__section">
            <div class="pbx-sites__section-row">
              <h3 class="pbx-sites__section-title">토큰</h3>
              <div class="pbx-sites__actions">
                <button
                  v-if="previousTokenActive(selected)"
                  type="button"
                  class="pbx-sites__ghost pbx-sites__ghost--danger"
                  :disabled="selectedRevoked || isPending(`revokePrev:${selected.id}`)"
                  @click="confirmRevokePrevious(selected)"
                >
                  이전 토큰 폐기
                </button>
                <button
                  type="button"
                  class="pbx-sites__ghost"
                  :disabled="selectedRevoked || isPending(`rotate:${selected.id}`)"
                  @click="openRotate(selected)"
                >
                  토큰 회전
                </button>
              </div>
            </div>
            <dl class="pbx-sites__info">
              <div>
                <dt>버전</dt>
                <dd>{{ selected.token_version }}</dd>
              </div>
              <div v-if="selected.previous_token_valid_until != null">
                <dt>이전 토큰 유효기한</dt>
                <dd :title="formatPbxRelative(selected.previous_token_valid_until, now)">
                  {{ formatPbxTime(selected.previous_token_valid_until) }}
                  <span v-if="!previousTokenActive(selected)" class="pbx-sites__dd-hint">만료됨</span>
                </dd>
              </div>
              <div>
                <dt>미전환</dt>
                <dd :class="{ 'pbx-sites__warn': selected.unmigrated_device_count > 0 }">
                  {{ selected.unmigrated_device_count ?? 0 }}대
                </dd>
              </div>
            </dl>
          </div>

          <!-- 삭제 -->
          <div class="pbx-sites__section">
            <div class="pbx-sites__section-row">
              <div>
                <h3 class="pbx-sites__section-title pbx-sites__section-title--danger">삭제</h3>
                <p class="pbx-sites__section-desc">반납되지 않은 전화기가 있으면 삭제할 수 없습니다.</p>
              </div>
              <button
                type="button"
                class="pbx-sites__ghost pbx-sites__ghost--danger"
                :disabled="selectedRevoked || isPending(`delete:${selected.id}`)"
                @click="confirmDelete(selected)"
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      </template>
    </section>

    <PbxSiteFormModal
      :open="form.open"
      :mode="form.mode"
      :site="formSite"
      :submitting="formSubmitting"
      :error="form.error"
      @close="form.open = false"
      @submit-create="submitCreate"
      @submit-update="submitUpdate"
    />

    <PbxSiteRotateModal
      :open="rotate.open"
      :submitting="rotateSubmitting"
      :error="rotate.error"
      @close="rotate.open = false"
      @request="confirmRotate"
    />

    <PbxSiteUrlModal
      :open="urlModal.open"
      :title="urlModal.title"
      :url="urlModal.url"
      @close="urlModal.open = false"
      @after-close="clearUrl"
    />
  </div>
</template>


<script setup>
import { EnvironmentOutlined, EyeOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons-vue';
import { Modal, message } from 'ant-design-vue';
import { computed, onBeforeUnmount, reactive, ref, watch } from 'vue';

import { getCallApiErrorBody, getCallApiErrorCode } from '@/views/Callbot/service/callApiError';
import { usePbxAction } from '@/views/Callbot/service/usePbxAction';
import {
  formatPbxRelative,
  formatPbxTime,
  siteStatusBadge,
  transportBadge,
  compareLabel,
} from '@/views/Callbot/service/pbxExtensions';

import PbxBadge from './PbxBadge.vue';
import PbxSiteFormModal from './PbxSiteFormModal.vue';
import PbxSiteRotateModal from './PbxSiteRotateModal.vue';
import PbxSiteUrlModal from './PbxSiteUrlModal.vue';

/**
 * 설치 장소(서버 리소스 이름은 site, pbx-sites) — 목록·생성·수정·삭제, 프로비저닝 URL 1회 표시, 토큰 회전.
 * 프로비저닝 URL 은 서버가 준 값을 그대로 보인다(조립하지 않음, 결정 11). 원문은 1회 모달에만
 * 두고 닫히면 비운다 — 토큰 원문이 화면 상태에 남지 않게.
 * 계약: ~/md/front/studio-내선·전화기·사이트-탭-요청.md "화면 명세 › 설치 장소", "요청 계약 › 설치 장소"
 */
const props = defineProps({ inventory: { type: Object, required: true } });
// inventory 는 호스트가 병원마다 :key 로 패널을 다시 마운트해 넘기는 고정 객체다 — 반응성 손실 없음
// eslint-disable-next-line vue/no-setup-props-destructure
const inv = props.inventory;
const { run, isPending: isActionPending } = usePbxAction(inv);

const LIST_ERROR_TEXT = '설치 장소 목록을 불러오지 못했습니다.';
const EMPTY_TEXT =
  '설치 장소가 없습니다. 전화기를 설치하는 장소(본원·분원 등)마다 하나 만들고, 그 프로비저닝 URL 을 전화기에 입력합니다.';
const MASKED_URL = '…/prov/****…';
const SITE_HAS_DEVICES_TEXT = '반납되지 않은 전화기가 있어 삭제할 수 없습니다.';
const REVOKED_TEXT = '삭제(폐기)된 설치 장소입니다. 프로비저닝 URL 은 더 이상 유효하지 않고, 설정을 바꾸거나 다시 삭제할 수 없습니다.';

// 조작 키 — 설치 장소별로 나눠 한 설치 장소의 조작이 다른 설치 장소 버튼을 막지 않게
const key = (op, id) => (id ? `site:${op}:${id}` : `site:${op}`);
const isPending = (k) => isActionPending(`site:${k}`);

// ---------------------------------------------------------------------------
// 목록
// ---------------------------------------------------------------------------

// 서버는 최신순으로 준다 — 화면은 이름순이 찾기 쉽다.
// 삭제한 설치 장소는 서버에 status=revoked 로 남아 목록에 계속 온다(정본: DELETE = revoked, 토큰 두 개 모두 끊음).
// 운영자가 "삭제했는데 왜 남아 있나" 하지 않게 기본은 사용 중만 보이고, 폐기는 토글로 본다(전화기 탭 '반납 포함' 과 같다).
const sortedSites = computed(() => [...inv.sites.items.value].sort((a, b) => compareLabel(a.name, b.name)));
const revokedCount = computed(() => sortedSites.value.filter((s) => s.status === 'revoked').length);
const showRevoked = ref(false);
const sites = computed(() => (showRevoked.value ? sortedSites.value : sortedSites.value.filter((s) => s.status !== 'revoked')));
const now = computed(() => inv.now.value);
const listLoading = computed(() => inv.sites.loading.value);
const listError = computed(() => inv.sites.error.value);
// 판정 전(stage7 null)도 첫 로딩으로 본다 — 호스트는 stage7 이 false 가 아닐 때만 이 패널을 그린다
const firstLoading = computed(
  () => !inv.sites.loaded.value && (inv.stage7.value === null || inv.sites.loading.value)
);

/** 설치 장소별 전화기 수 — 반납은 빼고 센다. 전화기 목록을 아직 못 받았으면 null(`—`) */
const deviceCountBySite = computed(() => {
  if (!inv.devices.loaded.value) return null;
  const m = new Map();
  for (const d of inv.devices.items.value) {
    if (d.status === 'returned') continue;
    m.set(d.site_id, (m.get(d.site_id) ?? 0) + 1);
  }
  return m;
});

function deviceCountLabel(siteId) {
  const m = deviceCountBySite.value;
  return m ? `${m.get(siteId) ?? 0}대` : '—';
}

function siteTransport(s) {
  return transportBadge(s.sip_transport_default, s.sip_port_default);
}

function gmtOffsetLabel(sec) {
  if (sec == null) return '—';
  const abs = Math.abs(sec);
  const h = Math.floor(abs / 3600);
  const m = Math.floor((abs % 3600) / 60);
  const hm = m ? `${h}:${String(m).padStart(2, '0')}` : `${h}`;
  return `${sec}초 (UTC${sec < 0 ? '-' : '+'}${hm})`;
}

/** 이전 토큰 유예가 아직 진행 중인가 — `[이전 토큰 폐기]` 는 이때만 보인다 */
function previousTokenActive(s) {
  return s.previous_token_valid_until != null && s.previous_token_valid_until > now.value;
}

async function retry() {
  await inv.reload(['sites']);
  // 첫 판정이 실패였다면 전화기는 아직 안 받았다(7단계 판정 뒤에만 부른다) — 이어서 받는다
  if (inv.stage7.value) await inv.reload(['devices']);
}

// 조작 응답을 목록에 바로 얹는다 — 이어지는 재조회가 서버 값으로 다시 맞춘다
function patchSite(id, patch) {
  inv.sites.items.value = inv.sites.items.value.map((s) => (s.id === id ? { ...s, ...patch } : s));
}

// ---------------------------------------------------------------------------
// 선택 — 목록이 바뀌면 사라진 선택을 풀고, 비어 있으면 첫 설치 장소를 연다
// ---------------------------------------------------------------------------

const selectedId = ref(null);
const selected = computed(() => (selectedId.value ? inv.siteById.value.get(selectedId.value) ?? null : null));
// 폐기된 설치 장소는 읽기 전용 — 서버가 어차피 거절(404/409)하므로 버튼을 먼저 막고 이유를 보인다
const selectedRevoked = computed(() => selected.value?.status === 'revoked');

watch(
  sites,
  (list) => {
    if (selectedId.value && !list.some((s) => s.id === selectedId.value)) selectedId.value = null;
    if (!selectedId.value && list.length) selectedId.value = list[0].id;
  },
  { immediate: true }
);

// ---------------------------------------------------------------------------
// 1회 표시 URL 모달 — 닫힘 애니메이션이 끝나면 원문을 비운다
// ---------------------------------------------------------------------------

const urlModal = reactive({ open: false, title: '', url: '' });

function showUrl(title, url) {
  urlModal.title = title;
  urlModal.url = url;
  urlModal.open = true;
}

function clearUrl() {
  // 닫힘 직후 다른 URL 을 다시 열었으면 그 값은 지우지 않는다
  if (!urlModal.open) urlModal.url = '';
}

// admin 라우트는 KeepAlive 가 아니다 — 패널이 내려갈 때(병원 변경으로 다시 마운트되는 경우 포함)
// 감사 기록 없이 URL 이 다시 보이지 않게 즉시 닫고 원문을 비운다(닫힘 애니메이션을 기다리지 않는다)
onBeforeUnmount(() => {
  urlModal.open = false;
  urlModal.url = '';
});

async function viewUrl(site) {
  const res = await run(key('url', site.id), () => inv.api.getPbxSiteProvisioningUrl(site.id), {
    success: null,
    fallback: '프로비저닝 URL 을 불러오지 못했습니다.',
  });
  if (res) showUrl(`프로비저닝 URL — ${site.name}`, res.provisioning_url);
}

// ---------------------------------------------------------------------------
// 생성·수정 모달 — 실패는 모달 상단 인라인, 모달은 닫지 않는다
// ---------------------------------------------------------------------------

/** mode: 'create' | 'sip' | 'ips' */
const form = reactive({
  open: false,
  mode: 'create',
  siteId: null,
  error: null,
});

const formSite = computed(() =>
  form.mode === 'create' || !form.siteId ? null : inv.siteById.value.get(form.siteId) ?? null
);

const formSubmitting = computed(() =>
  form.mode === 'create' ? isPending('create') : !!form.siteId && isPending(`update:${form.siteId}`)
);

function openCreate() {
  form.mode = 'create';
  form.siteId = null;
  form.error = null;
  form.open = true;
}

function openEdit(mode, site) {
  form.mode = mode;
  form.siteId = site.id;
  form.error = null;
  form.open = true;
}

const inlineFormError = (_err, text) => {
  form.error = text;
  return true;
};

async function submitCreate(payload) {
  form.error = null;
  const res = await run(key('create'), () => inv.api.createPbxSite(payload), {
    success: null,
    reload: ['sites'],
    fallback: '설치 장소를 만들지 못했습니다.',
    onError: inlineFormError,
  });
  if (!res) return;
  // provisioning_url 은 목록 항목에 섞지 않는다 — 1회 모달에만
  const { provisioning_url: url, ...site } = res;
  if (!inv.sites.items.value.some((s) => s.id === site.id)) {
    inv.sites.items.value = [site, ...inv.sites.items.value];
  }
  selectedId.value = site.id;
  form.open = false;
  showUrl('설치 장소를 만들었습니다', url);
}

async function submitUpdate(payload) {
  const site = formSite.value;
  if (!site) return;
  form.error = null;
  const res = await run(key('update', site.id), () => inv.api.updatePbxSite(site.id, payload), {
    success: '설치 장소 수정을 완료했습니다.',
    fallback: '설치 장소를 수정하지 못했습니다.',
    onError: inlineFormError,
  });
  if (!res) return;
  patchSite(site.id, res);
  form.open = false;
  void inv.reload(['sites']);
}

// ---------------------------------------------------------------------------
// 토큰 회전 — 입력 모달 → 두 갈래 확인창 → 호출 → 1회 URL 모달
// ---------------------------------------------------------------------------

const rotate = reactive({
  open: false,
  siteId: null,
  error: null,
});

const rotateSubmitting = computed(() => !!rotate.siteId && isPending(`rotate:${rotate.siteId}`));

function openRotate(site) {
  rotate.siteId = site.id;
  rotate.error = null;
  rotate.open = true;
}

/**
 * 즉시 무효화 시 끊기는 전화기 수. 회전하면 모든 전화기가 아직 새 URL 을 받지 않은 상태가
 * 되므로(미전환 수는 회전 전 기준이라 작게 나온다) 설치 장소의 반납 안 된 전화기 전부를 센다.
 * 전화기 목록을 못 받았으면 서버 미전환 수로 대신한다.
 */
function cutOffCount(site) {
  const m = deviceCountBySite.value;
  return m ? m.get(site.id) ?? 0 : site.unmigrated_device_count ?? 0;
}

function confirmRotate(body) {
  const site = rotate.siteId ? inv.siteById.value.get(rotate.siteId) : undefined;
  if (!site) return;
  rotate.error = null;
  const revoke = body.revoke_previous;
  Modal.confirm({
    title: '토큰 회전',
    content: revoke
      ? `설치 장소 토큰을 회전하고 기존 URL 을 즉시 무효화합니다. 아직 새 URL 을 받지 않은 전화기 ${cutOffCount(site)}대는 설정을 받지 못하게 되며, 현장에서 3필드를 다시 입력하거나 복구 코드로 복구해야 합니다. 오프보딩이 아니면 취소하세요. 계속할까요?`
      : `설치 장소 토큰을 회전합니다. 새 URL 은 이 창에서 한 번만 보입니다. 기존 URL 은 ${body.grace_hours}시간 동안 유효하며, 그 안에 전화기들이 새 URL 로 옮겨가야 합니다(미전환 대수는 설치 장소 상세에 표시). 계속할까요?`,
    okText: revoke ? '즉시 무효화' : '회전',
    okType: revoke ? 'danger' : 'primary',
    cancelText: '취소',
    onOk: () => doRotate(site, body),
  });
}

/** 409 PBX_SITE_ROTATION_IN_GRACE 문구 — details 가 비면 설치 장소 항목 값으로 채운다 */
function rotationInGraceText(err, site) {
  const details = getCallApiErrorBody(err)?.error.details;
  const until =
    typeof details?.previous_token_valid_until === 'number'
      ? details.previous_token_valid_until
      : site.previous_token_valid_until;
  const count =
    typeof details?.unmigrated_device_count === 'number'
      ? details.unmigrated_device_count
      : site.unmigrated_device_count ?? 0;
  return `이전 토큰 유예가 ${formatPbxTime(until)}까지 진행 중이고 미전환 전화기가 ${count}대 있습니다. 지금 회전하려면 [기존 URL 즉시 무효]를 켜세요.`;
}

async function doRotate(site, body) {
  const res = await run(key('rotate', site.id), () => inv.api.rotatePbxSiteToken(site.id, body), {
    success: null,
    fallback: '토큰을 회전하지 못했습니다.',
    onError: (err, text) => {
      rotate.error =
        getCallApiErrorCode(err) === 'PBX_SITE_ROTATION_IN_GRACE' ? rotationInGraceText(err, site) : text;
      return true;
    },
  });
  if (!res) return;
  // 응답은 설치 장소 항목 전체가 아니다 — 토큰 섹션만 바로 바꾸고 목록은 다시 받는다
  patchSite(site.id, {
    token_version: res.token_version,
    previous_token_valid_until: res.previous_token_valid_until,
  });
  rotate.open = false;
  showUrl(`새 프로비저닝 URL — ${site.name}`, res.provisioning_url);
  void inv.reload(['sites']);
}

// ---------------------------------------------------------------------------
// 이전 토큰 폐기 · 삭제
// ---------------------------------------------------------------------------

function confirmRevokePrevious(site) {
  Modal.confirm({
    title: '이전 토큰 폐기',
    content: `이전 토큰(${formatPbxTime(site.previous_token_valid_until)}까지 유효)을 지금 폐기합니다. 아직 새 URL 을 받지 않은 전화기 ${site.unmigrated_device_count ?? 0}대는 설정을 받지 못하게 되며, 현장에서 3필드를 다시 입력하거나 복구 코드로 복구해야 합니다. 계속할까요?`,
    okText: '폐기',
    okType: 'danger',
    cancelText: '취소',
    onOk: () => doRevokePrevious(site),
  });
}

async function doRevokePrevious(site) {
  const res = await run(key('revokePrev', site.id), () => inv.api.revokePreviousPbxSiteToken(site.id), {
    success: '이전 토큰 폐기를 완료했습니다.',
    fallback: '이전 토큰을 폐기하지 못했습니다.',
  });
  if (!res) return;
  patchSite(site.id, res);
  void inv.reload(['sites']);
}

function confirmDelete(site) {
  Modal.confirm({
    title: '설치 장소 삭제',
    content: `설치 장소 ${site.name} 을 삭제합니다. 반납되지 않은 전화기가 있으면 삭제할 수 없습니다. 계속할까요?`,
    okText: '삭제',
    okType: 'danger',
    cancelText: '취소',
    onOk: () => doDelete(site),
  });
}

async function doDelete(site) {
  // 204(본문 없음) — 성공 여부를 반환값으로 가르려고 true 로 바꾼다
  const ok = await run(
    key('delete', site.id),
    async () => {
      await inv.api.deletePbxSite(site.id);
      return true;
    },
    {
      success: '설치 장소 삭제를 완료했습니다.',
      reload: ['sites'],
      fallback: '설치 장소를 삭제하지 못했습니다.',
      onError: (err) => {
        if (getCallApiErrorCode(err) !== 'PBX_SITE_HAS_DEVICES') return false;
        message.error(SITE_HAS_DEVICES_TEXT);
        // 전화기 수가 낡았을 수 있다 — 목록의 수를 서버 기준으로 맞춘다
        void inv.reload(['devices']);
        return true;
      },
    }
  );
  if (!ok) return;
  inv.sites.items.value = inv.sites.items.value.filter((s) => s.id !== site.id);
}
</script>

<style scoped lang="scss">
.pbx-sites {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: 320px minmax(0, 1fr);
  gap: 16px;
  font-size: 13px;

  &__list {
    display: flex;
    flex-direction: column;
    min-height: 0;
    min-width: 0;
    border: 1px solid #f0f0f0;
    border-radius: 12px;
    background: #fff;
    overflow: hidden;
  }

  &__list-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 14px;
    border-bottom: 1px solid #f0f0f0;
  }

  &__filter {
    flex: none;
    font-size: 12px;
  }

  &__title {
    flex: 1;
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    color: #262626;
  }

  &__add {
    flex: none;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 12px;
    border: 1px solid #1668dc;
    border-radius: 8px;
    background: #1668dc;
    color: #fff;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
      background: #0f5bc4;
    }
  }

  &__mini {
    flex: none;
    width: 28px;
    height: 28px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    background: #fff;
    color: #595959;
    cursor: pointer;

    &:hover:not(:disabled) {
      border-color: #bfbfbf;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &__banner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin: 8px 8px 0;
    padding: 8px 10px;
    border-radius: 8px;
    background: #fff2f0;
    color: #cf1322;
    font-size: 12px;
  }

  &__banner.pbx-sites__banner--muted {
    background: #fafafa;
    color: #8c8c8c;
  }

  &__link {
    flex: none;
    padding: 0;
    border: none;
    background: none;
    color: #1668dc;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &__items {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 8px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__item {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
    height: auto; // 전역 button { height: 40px } 덮어쓰기 — 없으면 FQDN·메타 줄이 다음 항목과 겹침
    padding: 10px 12px;
    border: none;
    border-radius: 8px;
    background: transparent;
    text-align: left;
    cursor: pointer;

    &:hover {
      background: #fafafa;
    }

    &.is-active {
      background: #f0f5ff;
    }

    &-top {
      display: flex;
      align-items: center;
      gap: 8px;
      min-width: 0;
    }

    &-name {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: 14px;
      font-weight: 600;
      color: #262626;
    }

    &-fqdn {
      font-size: 12px;
      color: #8c8c8c;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &-meta {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
      font-size: 11px;
      color: #8c8c8c;
    }
  }

  &__state {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 40px 16px;
    color: #8c8c8c;
    font-size: 13px;
    line-height: 1.6;
    text-align: center;

    p {
      margin: 0;
    }

    &--error {
      color: #cf1322;
    }
  }

  &__detail {
    min-height: 0;
    min-width: 0;
    overflow-y: auto;
    border: 1px solid #f0f0f0;
    border-radius: 12px;
    background: #fff;
    padding: 24px;
  }

  &__empty {
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    text-align: center;
    color: #595959;

    &-icon {
      font-size: 40px;
      color: #d9d9d9;
    }

    p {
      margin: 4px 0 0;
      font-size: 16px;
      font-weight: 600;
      color: #262626;
    }

    small {
      max-width: 380px;
      font-size: 13px;
      color: #8c8c8c;
      line-height: 1.6;
    }
  }

  &__detail-header {
    margin-bottom: 20px;
  }

  &__detail-title {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    color: #262626;
    word-break: break-all;
  }

  &__detail-sub {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 6px;
    font-size: 12px;
    color: #8c8c8c;
  }

  &__sections {
    display: flex;
    flex-direction: column;
    gap: 24px;
    max-width: 640px;
  }

  &__section {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding-bottom: 20px;
    border-bottom: 1px solid #fafafa;

    &:last-child {
      border-bottom: none;
      padding-bottom: 0;
    }
  }

  &__section-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
  }

  &__section-title {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    color: #262626;

    &--danger {
      color: #cf1322;
    }
  }

  &__section-desc {
    margin: 2px 0 0;
    font-size: 12px;
    color: #8c8c8c;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  &__masked {
    align-self: flex-start;
    padding: 6px 10px;
    border: 1px solid #f0f0f0;
    border-radius: 8px;
    background: #fafafa;
    color: #595959;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12px;
  }

  &__info {
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;

    div {
      display: flex;
      gap: 12px;
      min-width: 0;
    }

    dt {
      flex: none;
      width: 150px;
      font-size: 13px;
      font-weight: 400;
      color: #8c8c8c;
    }

    dd {
      flex: 1;
      min-width: 0;
      margin: 0;
      font-size: 13px;
      color: #262626;
      word-break: break-all;
    }
  }

  &__dd-hint {
    display: block;
    margin-top: 2px;
    font-size: 12px;
    color: #8c8c8c;
  }

  &__warn {
    color: #d48806;
    font-weight: 600;
  }

  &__ips {
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;

    li {
      padding: 2px 10px;
      border: 1px solid #f0f0f0;
      border-radius: 999px;
      background: #fafafa;
      font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
      font-size: 12px;
      color: #262626;
    }
  }

  &__muted {
    margin: 0;
    font-size: 13px;
    color: #8c8c8c;
  }

  &__ghost {
    flex: none;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    background: #fff;
    color: #595959;
    font-size: 13px;
    white-space: nowrap;
    cursor: pointer;

    &:hover:not(:disabled) {
      border-color: #bfbfbf;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &--danger {
      color: #cf1322;

      &:hover:not(:disabled) {
        border-color: #cf1322;
      }
    }
  }
}

@media (max-width: 1024px) {
  .pbx-sites {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
